import { useState } from "react";
import { http, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { UploadSimple, FileArrowUp, ArrowCounterClockwise } from "@phosphor-icons/react";

const SAMPLES = {
  generic: `symbol,exchange,quantity,avg_price,asset_type,name,notes
TCS,NSE,10,3200,Stock,Tata Consultancy,IT bet
INFY,NSE,20,1450,Stock,Infosys,
RELIANCE,NSE,5,2800,Stock,Reliance Industries,Long-term`,
  zerodha: `Instrument,Qty.,Avg. cost,LTP,Cur. val,P&L
WIPRO,10,400.00,412.55,4125.50,125.50
BAJAJHIND,50,25.10,28.50,1425.00,170.00`,
  groww: `Stock Name,ISIN,Quantity,Avg. buy price
Infosys,INE009A01021,20,1450.25
HDFC Bank,INE040A01034,5,1620`,
  icici_direct: `Stock Code,Stock Name,Quantity,Average Price
TATA,Tata Motors,15,850.10
RELIN,Reliance Industries,3,2800`,
};

const BROKER_LABELS = {
  generic: "Generic / Sirbansal",
  zerodha: "Zerodha Console",
  groww: "Groww",
  icici_direct: "ICICI Direct",
};

export default function CSVImportDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [broker, setBroker] = useState("auto");
  const [sampleSel, setSampleSel] = useState("generic");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsv(await f.text());
    setResult(null);
  };

  const undo = async (ids) => {
    if (!ids?.length) return;
    try {
      const { data } = await http.post("/portfolio/holdings/bulk-delete", { ids });
      toast.success(`Reverted ${data.deleted} holding${data.deleted === 1 ? "" : "s"}`);
      onImported?.();
      setResult(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    if (!csv.trim()) { toast.error("Paste CSV or choose a file first."); return; }
    setBusy(true);
    try {
      const body = { csv_text: csv };
      if (broker !== "auto") body.broker = broker;
      const { data } = await http.post("/portfolio/holdings/import", body);
      setResult(data);
      if (data.created_count > 0) {
        onImported?.();
        toast.success(
          `Imported ${data.created_count} holdings from ${BROKER_LABELS[data.broker_detected] || data.broker_detected}`,
          {
            action: {
              label: "Undo",
              onClick: () => undo(data.created_ids),
            },
            duration: 12000,
            id: `csv-undo-${Date.now()}`,
          }
        );
      } else {
        toast.error("No rows were imported. Check the errors below.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const close = () => { setOpen(false); setCsv(""); setResult(null); setBroker("auto"); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:text-zinc-100" data-testid="import-csv-button">
          <UploadSimple size={16} className="mr-2" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import holdings from CSV</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Paste or upload a broker statement. Broker format is auto-detected — Zerodha, Groww, ICICI Direct, or generic.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Force broker format</Label>
              <Select value={broker} onValueChange={setBroker}>
                <SelectTrigger data-testid="csv-broker-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="auto">Auto-detect (recommended)</SelectItem>
                  <SelectItem value="zerodha">Zerodha Console</SelectItem>
                  <SelectItem value="groww">Groww</SelectItem>
                  <SelectItem value="icici_direct">ICICI Direct</SelectItem>
                  <SelectItem value="generic">Generic Sirbansal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Load a sample</Label>
              <div className="flex gap-1 mt-1">
                <Select value={sampleSel} onValueChange={setSampleSel}>
                  <SelectTrigger data-testid="csv-sample-select" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="generic">Generic</SelectItem>
                    <SelectItem value="zerodha">Zerodha</SelectItem>
                    <SelectItem value="groww">Groww</SelectItem>
                    <SelectItem value="icici_direct">ICICI Direct</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="bg-zinc-900 border-zinc-800 shrink-0"
                  onClick={() => { setCsv(SAMPLES[sampleSel]); setResult(null); }}
                  data-testid="load-sample-csv">
                  Load
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-zinc-800 bg-zinc-900/50 p-3 text-xs">
            <div className="text-zinc-400 mb-1">Supported broker exports:</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-500">
              <div>• <span className="text-emerald-300">Zerodha</span> — <span className="font-mono">Instrument, Qty., Avg. cost</span></div>
              <div>• <span className="text-emerald-300">Groww</span> — <span className="font-mono">Stock Name, ISIN, Quantity, Avg. buy price</span></div>
              <div>• <span className="text-emerald-300">ICICI Direct</span> — <span className="font-mono">Stock Code, Stock Name, Quantity, Average Price</span></div>
              <div>• <span className="text-emerald-300">Generic</span> — <span className="font-mono">symbol, quantity, avg_price</span></div>
            </div>
          </div>

          <div>
            <Label className="text-zinc-300">Upload a .csv file</Label>
            <div className="mt-2 flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-sm">
                <FileArrowUp size={16} /> Choose file
                <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-file-input" />
              </label>
            </div>
          </div>

          <div>
            <Label className="text-zinc-300">Or paste CSV text</Label>
            <Textarea
              data-testid="csv-textarea"
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={8}
              placeholder={SAMPLES.generic}
              className="mt-2 bg-zinc-900 border-zinc-800 font-mono text-xs"
            />
          </div>

          {result && (
            <div className="rounded-md border border-emerald-900/40 bg-emerald-950/30 p-3 text-sm" data-testid="import-result">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-emerald-300 font-medium">
                    Imported {result.created_count} holdings
                    <span className="ml-2 text-xs uppercase tracking-widest text-emerald-500 font-mono">
                      via {BROKER_LABELS[result.broker_detected] || result.broker_detected}
                    </span>
                  </div>
                  {result.created_symbols?.length > 0 && (
                    <div className="text-zinc-400 text-xs mt-1">Symbols: {result.created_symbols.join(", ")}</div>
                  )}
                </div>
                {result.created_count > 0 && (
                  <Button size="sm" variant="outline" onClick={() => undo(result.created_ids)}
                    className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800" data-testid="undo-import-inline">
                    <ArrowCounterClockwise size={14} className="mr-1.5" /> Undo
                  </Button>
                )}
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-red-400 text-xs">
                  <div className="font-medium">{result.errors.length} row(s) failed:</div>
                  <ul className="mt-1 list-disc list-inside">
                    {result.errors.slice(0, 5).map((er) => (
                      <li key={`err-row-${er.row}`}>Row {er.row}: {er.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>Close</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy}
            onClick={submit} data-testid="csv-import-submit">
            {busy ? "Importing…" : "Import holdings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
